import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2018', event: '蘑菇视频App正式上线，首月用户突破100万', icon: 'ri-rocket-line' },
  { year: '2019', event: '日活跃用户突破500万，完成A轮融资', icon: 'ri-funds-line' },
  { year: '2020', event: '用户总量突破3000万，推出直播和创作工具功能', icon: 'ri-live-line' },
  { year: '2021', event: '上线智能推荐系统，用户日均使用时长超过45分钟', icon: 'ri-brain-line' },
  { year: '2022', event: '总用户突破5000万，获评年度最佳短视频应用', icon: 'ri-award-line' },
  { year: '2023', event: '推出AI创作助手，创作者数量超过80万', icon: 'ri-robot-line' },
  { year: '2024', event: '国际化布局启动，覆盖东南亚多个国家', icon: 'ri-global-line' },
  { year: '2025', event: '月活用户突破8000万，内容生态持续完善', icon: 'ri-bar-chart-line' },
  { year: '2026', event: '发布v5.2版本，全面升级AI推荐与创作体验', icon: 'ri-sparkling-line' },
];

const values = [
  { title: '用户至上', desc: '始终将用户体验放在首位，不断优化产品功能和服务质量', icon: 'ri-heart-line' },
  { title: '创新驱动', desc: '持续投入技术研发，用AI和数据驱动产品创新', icon: 'ri-lightbulb-line' },
  { title: '内容为王', desc: '坚持优质内容生态建设，为创作者提供最好的平台支持', icon: 'ri-star-line' },
  { title: '安全可靠', desc: '建立完善的内容审核和数据保护机制，守护每位用户', icon: 'ri-shield-check-line' },
];

const reviews = [
  {
    name: '张先生',
    role: '资深用户',
    content: '用了三年蘑菇视频，真心觉得这是国内体验最好的短视频App。界面简洁清爽，推荐算法很懂我，每天刷视频都是享受。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '内容创作者',
    content: '作为一个拥有20万粉丝的创作者，蘑菇视频的创作工具真的帮了我大忙。剪辑流畅、特效丰富，让我的创作效率大大提升！',
    rating: 5,
  },
  {
    name: '王同学',
    role: '大学生用户',
    content: '我是通过室友推荐开始用的，没想到一用就停不下来。离线下载功能在通勤路上特别好用，节省了好多流量。',
    rating: 5,
  },
  {
    name: '赵老师',
    role: '教育领域用户',
    content: '蘑菇视频的青少年模式做得非常用心，时间管理和内容过滤都很好，让我可以放心地推荐给学生和家长使用。',
    rating: 5,
  },
  {
    name: '陈先生',
    role: '企业用户',
    content: '播放流畅度在同类型产品中绝对是顶尖的，即使在弱网环境下也能保持清晰流畅的播放效果，这一点特别满意。',
    rating: 5,
  },
  {
    name: '刘女士',
    role: '活跃用户',
    content: 'VIP会员很超值，免广告加上超清画质让体验直接翻倍。客服响应也很快，遇到问题都能及时解决。',
    rating: 5,
  },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于蘑菇视频',
  description: '蘑菇视频是一款备受用户喜爱的短视频娱乐平台，自2018年上线以来已服务超过8000万用户。',
};

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 蘑菇视频"
        description="蘑菇视频App品牌介绍、发展历程、核心价值与联系方式。自2018年成立以来致力于打造国内领先的短视频娱乐平台。"
        keywords="蘑菇视频app,关于蘑菇视频,蘑菇视频品牌介绍,蘑菇视频联系方式"
        canonicalUrl="/about"
      />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <Navbar />

      <main className="pt-24 pb-16 min-h-screen bg-background-50">
        <div className="container-page">
          <div className="mb-6">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          </div>

          <section id="brand" className="mb-20">
            <div className="text-center mb-12">
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-primary-500 flex items-center justify-center">
                <i className="ri-play-circle-fill text-4xl text-white"></i>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mb-4">
                关于蘑菇视频
              </h1>
              <p className="text-foreground-500 max-w-3xl mx-auto text-base leading-relaxed">
                蘑菇视频成立于2018年，是国内领先的短视频娱乐平台。我们致力于通过技术创新和优质内容，为每一位用户带来极致的视频观看与创作体验。目前已服务超过8000万用户，平台汇聚了超过100万内容创作者，覆盖搞笑、美食、旅游、音乐、科技等20多个内容品类。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
              {[
                { value: '8000万+', label: '累计用户' },
                { value: '100万+', label: '内容创作者' },
                { value: '20+', label: '内容品类' },
                { value: '8年', label: '行业深耕' },
              ].map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-1.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <h2 className="text-xl font-semibold text-foreground-900 mb-6 font-heading">品牌价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((val) => (
                <div key={val.title} className="bg-background-100 rounded-xl p-6">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${val.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-xl font-semibold text-foreground-900 mb-8 font-heading text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="flex flex-col gap-6">
                {milestones.map((m, i) => (
                  <div
                    key={m.year}
                    className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-100 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-2 md:justify-end">
                          <span className="text-sm font-bold text-primary-600">{m.year}</span>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-8 h-8 rounded-full bg-primary-500 flex-shrink-0 items-center justify-center relative z-10">
                      <i className={`${m.icon} text-sm text-white`}></i>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-xl font-semibold text-foreground-900 mb-8 font-heading text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((review, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, si) => (
                      <i key={si} className="ri-star-fill text-sm text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.content}</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary-600">{review.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="bg-background-100 rounded-2xl p-8 md:p-12">
            <h2 className="text-xl font-semibold text-foreground-900 mb-8 font-heading text-center">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-1.5 font-heading">电子邮箱</h3>
                <p className="text-sm text-foreground-500">support@mogushipin.com</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-phone-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-1.5 font-heading">客服热线</h3>
                <p className="text-sm text-foreground-500">400-888-8888</p>
                <p className="text-xs text-foreground-400 mt-1">工作时间：每日 9:00 - 21:00</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-1.5 font-heading">公司地址</h3>
                <p className="text-sm text-foreground-500">北京市朝阳区科技园A座</p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}